/**
 * MediaImage — safe image tile that swaps to a "photo unavailable"
 * placeholder on any load error. Use for any backend-hosted photo where a
 * 404 / permission / network error is possible.
 *
 *   <MediaImage
 *     src={resolveMediaUrl(row.photo_url)}
 *     alt="Front Door"
 *     className="w-24 h-16 object-cover border border-[#27272A]"
 *   />
 *
 * The fallback keeps the original className so the parent's grid stays intact.
 */
import { useState } from "react";
import { ImageOff } from "lucide-react";

export default function MediaImage({
  src,
  alt = "",
  className = "",
  fallbackClassName = "",
  testId,
  onLoad,
  onError,
  ...rest
}) {
  const [broken, setBroken] = useState(false);

  if (!src || broken) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 border border-dashed border-[#27272A] bg-[#0A0A0A] text-zinc-500 ${className} ${fallbackClassName}`}
        data-testid={testId ? `${testId}-fallback` : "media-image-fallback"}
        role="img"
        aria-label={`${alt} — photo unavailable`}
      >
        <ImageOff className="size-4 opacity-60" />
        <span className="text-[9px] uppercase tracking-widest">photo unavailable</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onLoad={onLoad}
      onError={(e) => {
        setBroken(true);
        if (onError) onError(e);
      }}
      data-testid={testId}
      {...rest}
    />
  );
}
